import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, AlertCircle, Ship, Package, Anchor, Download, ChevronRight, FileText, CheckCircle2 } from 'lucide-react';
import * as XLSX from 'xlsx';

// ============================================================================
// PRODUCTION PIPELINE TRACKER - Pizza Tracker style lifecycle view
// ============================================================================

const STAGES = [
  { key: 'ISSUED',   label: 'Issued',   icon: FileText,    bg: 'bg-amber-50',  bgActive: 'bg-amber-100', border: 'border-amber-200', borderActive: 'border-amber-500', ring: 'ring-amber-200', text: 'text-amber-700',  badge: 'bg-amber-100 text-amber-700' },
  { key: 'BOOKED',   label: 'Booked',   icon: Anchor,      bg: 'bg-blue-50',   bgActive: 'bg-blue-100',  border: 'border-blue-200',  borderActive: 'border-blue-500',  ring: 'ring-blue-200',  text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700' },
  { key: 'SHIPPED',  label: 'Shipped',  icon: Ship,        bg: 'bg-cyan-50',   bgActive: 'bg-cyan-100',  border: 'border-cyan-200',  borderActive: 'border-cyan-500',  ring: 'ring-cyan-200',  text: 'text-cyan-700',   badge: 'bg-cyan-100 text-cyan-700' },
  { key: 'RECEIVED', label: 'Received', icon: CheckCircle2, bg: 'bg-green-50',  bgActive: 'bg-green-100', border: 'border-green-200', borderActive: 'border-green-500', ring: 'ring-green-200', text: 'text-green-700',  badge: 'bg-green-100 text-green-700' },
];

export default function ProductionPipelineDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [pipelineSummary, setPipelineSummary] = useState([]);
  const [totals, setTotals] = useState({});
  const [filters, setFilters] = useState({ divisions: [], seasons: [], customers: [], factories: [] });

  // Filters - default to Target, Division 10, SP26 for testing
  const [selectedDivision, setSelectedDivision] = useState('1.InMocean');
  const [selectedSeason, setSelectedSeason] = useState('SP26');
  const [selectedCustomer, setSelectedCustomer] = useState('TARGET');
  const [selectedFactory, setSelectedFactory] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null); // client-side filter

  const fmt = (v) => v == null ? '0' : new Intl.NumberFormat('en-US').format(Math.round(v));
  const fmtCur = (v) => {
    if (v == null || v === 0) return '$0';
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };
  const fmtDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const fmtPct = (v) => v == null ? '0%' : `${v.toFixed(1)}%`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'getProductionPipelineTracker', { timeout: 150000 });
      const result = await fn({
        division: selectedDivision,
        season: selectedSeason,
        customer: selectedCustomer,
        factory: selectedFactory
      });
      const d = result.data;
      setData(d.data || []);
      setPipelineSummary(d.pipelineSummary || []);
      setTotals(d.totals || {});
      setFilters(d.filters || { divisions: [], seasons: [], customers: [], factories: [] });
    } catch (err) {
      console.error('Error fetching pipeline tracker:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedDivision, selectedSeason, selectedCustomer, selectedFactory]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearFilters = () => {
    setSelectedDivision(null);
    setSelectedSeason(null);
    setSelectedCustomer(null);
    setSelectedFactory(null);
    setSelectedStage(null);
  };

  const hasActiveFilters = selectedDivision || selectedSeason || selectedCustomer || selectedFactory || selectedStage;

  // Client-side stage filter
  const displayData = selectedStage ? data.filter(r => r.pipeline_stage === selectedStage) : data;

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const exportRows = displayData.map(r => ({
      'Stage': r.pipeline_stage,
      'PO': r.prod_num,
      'Line': r.open_seq,
      'Style': r.style || '',
      'Color': r.color || '',
      'Division': r.DivisionName || '',
      'Factory': r.factory || '',
      'Customer': r.customer || '',
      'Units': r.units,
      'Revenue': r.revenue,
      'Cost': r.cost,
      'GP': (Number(r.revenue) || 0) - (Number(r.cost) || 0),
      'Booking': r.Booking || '',
      'Cargo Turnover': r.Cargo_Turnover_Date || '',
      'ETD POL': r.ETD_POL || '',
      'ETA': r.ETA || '',
      'Ship Date': r.earliest_ship_date || ''
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), 'Pipeline');
    XLSX.writeFile(wb, `Production_Pipeline_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getStageConfig = (key) => STAGES.find(s => s.key === key) || STAGES[0];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Ship size={20} className="text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Production Pipeline Tracker</h2>
            <p className="text-xs text-slate-500">Track orders: Issued → Booked → Shipped → Received</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!loading && <span className="text-xs text-slate-400">{fmt(data.length)} PO lines</span>}
          <button onClick={handleExportExcel} disabled={loading || data.length === 0} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
            <Download size={12} /> Excel
          </button>
          <button onClick={fetchData} disabled={loading} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Pipeline Stage Flow */}
      {!loading && !error && pipelineSummary.length > 0 && (
        <div className="flex items-stretch gap-1">
          {STAGES.map((stage, idx) => {
            const stageData = pipelineSummary.find(s => s.stage === stage.key) || { poLines: 0, units: 0, revenue: 0, cost: 0 };
            const isActive = selectedStage === stage.key;
            const Icon = stage.icon;
            return (
              <React.Fragment key={stage.key}>
                <button
                  onClick={() => setSelectedStage(isActive ? null : stage.key)}
                  className={`flex-1 rounded-lg border-2 p-3 cursor-pointer transition-all text-left ${
                    isActive
                      ? `${stage.bgActive} ${stage.borderActive} ring-2 ${stage.ring}`
                      : `${stage.bg} ${stage.border} hover:${stage.bgActive}`
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon size={14} className={stage.text} />
                    <span className={`text-xs font-bold ${stage.text}`}>{stage.label}</span>
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-lg font-bold text-slate-800">{fmt(stageData.poLines)}</div>
                    <div className="text-[10px] text-slate-500">PO lines</div>
                    <div className="text-xs font-semibold text-slate-700 mt-1">{fmt(stageData.units)} units</div>
                    <div className={`text-xs font-semibold ${stage.text}`}>{fmtCur(stageData.revenue)} rev</div>
                    <div className="text-[10px] text-slate-500">{fmtCur(stageData.cost)} cost</div>
                  </div>
                </button>
                {idx < STAGES.length - 1 && (
                  <div className="flex items-center">
                    <ChevronRight size={20} className="text-slate-300" />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Summary Cards */}
      {!loading && !error && totals && (
        <div className="grid grid-cols-6 gap-3">
          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center shadow-sm">
            <p className="text-[10px] text-slate-500 font-medium mb-1">PO Lines</p>
            <p className="text-lg font-bold text-slate-800">{fmt(totals.totalPOLines)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center shadow-sm">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Package size={12} className="text-blue-500" />
              <p className="text-[10px] text-slate-500 font-medium">Total Units</p>
            </div>
            <p className="text-lg font-bold text-slate-800">{fmt(totals.totalUnits)}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3 text-center shadow-sm">
            <p className="text-[10px] text-emerald-600 font-medium mb-1">Revenue</p>
            <p className="text-lg font-bold text-emerald-700">{fmtCur(totals.totalRevenue)}</p>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 text-center shadow-sm">
            <p className="text-[10px] text-blue-600 font-medium mb-1">Cost</p>
            <p className="text-lg font-bold text-blue-700">{fmtCur(totals.totalCost)}</p>
          </div>
          <div className="bg-purple-50 rounded-lg border border-purple-200 p-3 text-center shadow-sm">
            <p className="text-[10px] text-purple-600 font-medium mb-1">GP</p>
            <p className="text-lg font-bold text-purple-700">{fmtCur(totals.totalGP)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center shadow-sm">
            <p className="text-[10px] text-slate-500 font-medium mb-1">GP%</p>
            <p className={`text-lg font-bold ${(totals.totalGPPct || 0) >= 30 ? 'text-green-600' : (totals.totalGPPct || 0) < 0 ? 'text-red-600' : 'text-slate-700'}`}>{fmtPct(totals.totalGPPct)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4 flex-wrap">
            {filters.divisions && filters.divisions.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-slate-500">Division:</span>
                <select value={selectedDivision || ''} onChange={(e) => setSelectedDivision(e.target.value || null)} className="text-xs border border-slate-200 rounded px-2 py-1">
                  <option value="">All</option>
                  {filters.divisions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}
            {filters.seasons && filters.seasons.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-slate-500">Season:</span>
                <select value={selectedSeason || ''} onChange={(e) => setSelectedSeason(e.target.value || null)} className="text-xs border border-slate-200 rounded px-2 py-1">
                  <option value="">All</option>
                  {filters.seasons.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {filters.customers && filters.customers.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-slate-500">Customer:</span>
                <select value={selectedCustomer || ''} onChange={(e) => setSelectedCustomer(e.target.value || null)} className="text-xs border border-slate-200 rounded px-2 py-1">
                  <option value="">All</option>
                  {filters.customers.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            {filters.factories && filters.factories.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-slate-500">Factory:</span>
                <select value={selectedFactory || ''} onChange={(e) => setSelectedFactory(e.target.value || null)} className="text-xs border border-slate-200 rounded px-2 py-1">
                  <option value="">All</option>
                  {filters.factories.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            )}
            {selectedStage && (
              <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-slate-200">
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${getStageConfig(selectedStage).badge}`}>{selectedStage}</span>
                <button onClick={() => setSelectedStage(null)} className="text-[10px] text-red-500 hover:text-red-700">x</button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button onClick={clearFilters} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">Reset</button>
            )}
          </div>
        </div>
      </div>

      {/* Detail Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-white flex items-center gap-2">
          <Package size={16} className="text-emerald-600" />
          <h3 className="text-sm font-semibold text-slate-800">Order Detail</h3>
          {selectedStage && <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${getStageConfig(selectedStage).badge}`}>{selectedStage}</span>}
          <span className="text-xs text-slate-400">{fmt(displayData.length)} rows</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw size={20} className="animate-spin text-emerald-500" />
            <span className="ml-2 text-sm text-slate-600">Loading pipeline data...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16">
            <AlertCircle size={20} className="text-red-500 mr-2" />
            <span className="text-sm text-red-600">{error}</span>
          </div>
        ) : displayData.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <span className="text-sm text-slate-500">No data found</span>
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ maxHeight: '600px' }}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr>
                  <th className="px-2 py-2 text-center font-semibold text-slate-600 border-b border-slate-200">Stage</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">PO</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Line</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Style</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Division</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Factory</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Customer</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Units</th>
                  <th className="px-2 py-2 text-right font-semibold text-emerald-600 border-b border-slate-200 bg-emerald-50/50">Revenue</th>
                  <th className="px-2 py-2 text-right font-semibold text-blue-600 border-b border-slate-200 bg-blue-50/50">Cost</th>
                  <th className="px-2 py-2 text-right font-semibold text-purple-600 border-b border-slate-200 bg-purple-50/50">GP</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Booking</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">Cargo Turnover</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">ETD</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">ETA</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">Ship Date</th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((row, idx) => {
                  const sc = getStageConfig(row.pipeline_stage);
                  const gp = (Number(row.revenue) || 0) - (Number(row.cost) || 0);
                  return (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-2 py-1.5 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${sc.badge}`}>{row.pipeline_stage}</span>
                      </td>
                      <td className="px-2 py-1.5 font-medium text-slate-700 whitespace-nowrap">{row.prod_num || '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{row.open_seq || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600">{row.style || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{row.DivisionName || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{row.factory || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{row.customer || '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{fmt(row.units)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">{fmtCur(row.revenue)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-blue-700">{fmtCur(row.cost)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${gp >= 0 ? 'text-purple-600' : 'text-red-600'}`}>{fmtCur(gp)}</td>
                      <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{row.Booking || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(row.Cargo_Turnover_Date)}</td>
                      <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(row.ETD_POL)}</td>
                      <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(row.ETA)}</td>
                      <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(row.earliest_ship_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300">
                <tr className="font-semibold text-slate-700">
                  <td className="px-2 py-2" colSpan={7}>TOTAL ({fmt(displayData.length)} rows)</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmt(displayData.reduce((s, r) => s + (Number(r.units) || 0), 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-emerald-700">{fmtCur(displayData.reduce((s, r) => s + (Number(r.revenue) || 0), 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-blue-700">{fmtCur(displayData.reduce((s, r) => s + (Number(r.cost) || 0), 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-purple-600">{fmtCur(displayData.reduce((s, r) => s + ((Number(r.revenue) || 0) - (Number(r.cost) || 0)), 0))}</td>
                  <td colSpan={5}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
